import * as React from "react";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { useEffect } from "react";
import { useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Modal,
  TablePagination,
  TextField,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";

import PageLoader from "../PageLoader";
import {
  getAllSettings,
  parseSettingRows,
  getCronJobsOrg,
  createCronJobOrg,
  deleteCronJobOrg,
  buildSyncJobOrg,
} from "../../utils/api";
import {
  BRAND_COLOR_TINT,
  WARNING_COLOR,
  WARNING_TINT_SOFT,
} from "../../utils/colors";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: BRAND_COLOR_TINT,
    color: "black",
    padding: "7px 5px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "0 5px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  minHeight: 200,
  transform: "translate(-50%, -50%)",
  minWidth: 460,
  bgcolor: "background.paper",
  // borderRadius: 5,
  boxShadow: 24,
  p: 4,
};

const ZOHO = window.ZOHO;

/** Historical-data jobs are identified by body.type (legacy rows by cv_id).
 *  A listed job IS running — v3 jobs delete themselves on completion. */
const isHistoricalJob = (job) =>
  job?.body?.type === "backfill" ||
  (job?.execute_in === 1 && job?.body?.cv_id);

export default function HistoricalData({
  orgId,
  zohoApiKey,
  dataCenterUrl,
  zapiKey,
  connectionUrl,
}) {
  const [loading, setLoading] = useState(false);
  const [handleSubmitLoading, setHandleSubmitLoading] = useState(false);
  const [toggle, setToggle] = useState(false);
  const [cronList, setCronList] = useState([]);
  const [individualCron, setIndividualCron] = useState({});
  const [fetchedBlocks, setFetchedBlocks] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState();
  const [customViews, setCustomViews] = useState([]);
  const [selectedViews, setSelectedViews] = useState();
  const [customViewFetchLoading, setCustomViewFetchLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    // always open with a clean slate — no stale selections from a previous run
    setSelectedModule();
    setSelectedViews();
    setCustomViews([]);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = (type, data) => {
    setIndividualCron({ type: type, data: data });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setIndividualCron({});
    setOpenModal(false);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const auth = { orgId, apiKey: zohoApiKey };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const visibleRows = React.useMemo(
    () => cronList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [cronList, page, rowsPerPage]
  );

  useEffect(() => {
    if (!selectedModule) {
      return;
    }
    setCustomViewFetchLoading(true);
    ZOHO.CRM.META.getCustomViews({ Entity: selectedModule }).then(function (
      data
    ) {
      setCustomViews(data?.custom_views);
      setCustomViewFetchLoading(false);
    });
  }, [selectedModule]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const settingRows = await getAllSettings(auth);
      const moduleSettings = parseSettingRows(settingRows);
      setFetchedBlocks(moduleSettings);

      const jobs = (await getCronJobsOrg(auth)) || [];

      setPage(0);
      setRowsPerPage(10);

      // show every job EXCEPT recurring module syncs (those belong to the
      // Settings tab) — backfill, restore and malformed rows stay visible
      // here so they can be inspected and deleted
      const isSyncJob = (job) =>
        job?.body?.type === "sync" ||
        (!job?.body?.type && !job?.body?.cv_id && job?.execute_in !== 1);
      setCronList(jobs.filter((job) => !isSyncJob(job)));

      // ...but only custom-view back-fills block a module from Bulk Process
      const historicalJobs = jobs.filter(isHistoricalJob);
      const runningModules = historicalJobs.map((j) => j?.body?.moduleName);

      const availableModulesForCron = moduleSettings
        ?.map((item) => item.module_name)
        ?.filter((element) => !runningModules.includes(element));

      setAvailableModules(availableModulesForCron);

      setLoading(false);
    };

    fetchData();
  }, [toggle]);

  const handleSubmit = async (module, cv) => {
    let blockData = fetchedBlocks.find((el) => el.module_name === module);

    try {
      setHandleSubmitLoading(true);
      await createCronJobOrg(
        auth,
        buildSyncJobOrg({
          auth,
          dataCenterUrl,
          zapiKey,
          // backfill jobs are handled by the dedicated backfill function —
          // syncModuleData now skips any job whose body.type isn't "sync"
          functionName: "backfillmoduledata",
          apiVersion: "v7",
          body: {
            cv_id: { name: cv.display_value, id: cv.id },
            moduleName: blockData.module_name, // Module Name
            pageNo: 0, // Always 0
            type: "backfill",
            connectionUrl,
          },
          executeIn: 1,
        })
      );

      setHandleSubmitLoading(false);
      handleClose();
      setToggle(!toggle);
    } catch (error) {
      console.log({ error });
    }
  };

  const handleCronUpdate = async () => {
    try {
      setHandleSubmitLoading(true);
      // v3 jobs have no "Stopped" state — removing the job stops the loop
      await deleteCronJobOrg(auth, individualCron?.data?.job_id);

      setHandleSubmitLoading(false);
      handleCloseModal();
      setToggle(!toggle);
    } catch (error) {}
  };

  if (loading) {
    return <PageLoader message="Loading historical data..." />;
  } else {
    return (
      <Box sx={{ py: 5 }}>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Box>
              <Typography
                sx={{ fontWeight: "bold", fontSize: 14, my: 1, mb: 3 }}
              >
                Please Select Modules and Custom View
              </Typography>
              <Autocomplete
                disablePortal
                id="bulk-process-module"
                options={availableModules || []}
                value={selectedModule || null}
                getOptionLabel={(option) => option}
                onChange={(_, data) => {
                  if (data) {
                    setSelectedModule(data);
                  } else {
                    setSelectedModule();
                    setCustomViews([]);
                    setSelectedViews();
                  }
                }}
                size="small"
                sx={{ width: 300, mb: 2 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Modules"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <Autocomplete
                disablePortal
                id="bulk-process-custom-view"
                loading={customViewFetchLoading}
                options={customViews}
                value={selectedViews || null}
                disabled={!selectedModule}
                size="small"
                getOptionLabel={(option) => option?.display_value}
                onChange={(_, data) => {
                  if (data) {
                    setSelectedViews(data);
                  } else {
                    setSelectedViews();
                  }
                }}
                sx={{ width: 300 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Custom Views"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  sx={{ mr: 2, width: 120 }}
                  size="small"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                {/* Save only exists once BOTH module and custom view are chosen */}
                {selectedModule && selectedViews?.id && (
                  <Button
                    size="small"
                    disabled={handleSubmitLoading}
                    onClick={() => handleSubmit(selectedModule, selectedViews)}
                    variant="contained"
                    sx={{ width: 120 }}
                  >
                    {handleSubmitLoading ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        loading
                        <CircularProgress
                          sx={{ color: "white", ml: 1 }}
                          size={17}
                        />{" "}
                      </Box>
                    ) : (
                      "Save"
                    )}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Modal>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          {" "}
          <Button
            onClick={handleOpen}
            variant="contained"
            size="medium"
            sx={{
              fontWeight: 500,
              fontFamily: "Lato",
              textTransform: "capitalize",
            }}
          >
            <AddIcon sx={{ mr: "5px", fontSize: "16px" }} />
            Bulk Process
          </Button>
        </Box>
        <TableContainer component={Paper} sx={{ m: 2 }}>
          <Table size="small" aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell align="left">Created Date</StyledTableCell>
                <StyledTableCell align="left">Module Name</StyledTableCell>
                <StyledTableCell align="left">Custom View Name</StyledTableCell>
                <StyledTableCell align="center">Status</StyledTableCell>
                <StyledTableCell align="center">Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cronList.length > 0 ? (
                visibleRows.map((row, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell align="left">
                      {row?.created_at
                        ? dayjs(row?.created_at).format("MM-DD-YYYY HH:mm")
                        : "—"}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {row?.body?.moduleName || "—"}
                      <Chip
                        size="small"
                        label={
                          row?.body?.type ||
                          (row?.body?.cv_id ? "backfill" : "sync")
                        }
                        sx={{ ml: 1, height: 18, fontSize: 11 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {row?.body?.cv_id?.name || "—"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {/* a listed v3 job is running — it deletes itself when done */}
                      <Chip
                        sx={{
                          fontWeight: 500,
                          width: 95,
                          height: 25,
                          bgcolor: WARNING_TINT_SOFT,
                          color: "black",
                        }}
                        label="In Progress"
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Tooltip title="Stop">
                        <StopCircleIcon
                          onClick={() => handleOpenModal("stop", row)}
                          sx={{
                            cursor: "pointer",
                            color: WARNING_COLOR,
                          }}
                        />
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} align="center">
                    <Typography sx={{ py: 1, fontWeight: "bold" }}>
                      {" "}
                      No Data Available
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10]}
          component="div"
          count={cronList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Box
              sx={{
                bgcolor: "white",
                borderRadius: 5,
              }}
            >
              {" "}
              <Typography sx={{ mt: 4, fontWeight: "bold", fontSize: 20 }}>
                Do you want to{" "}
                {individualCron.type === "stop" ? "stop" : "delete"} this log?
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  ml: "1rem",
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "1rem",
                }}
              >
                <Button
                  sx={{ width: 120 }}
                  variant="outlined"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>

                <Button
                  disabled={handleSubmitLoading}
                  sx={{ width: 120 }}
                  variant="contained"
                  onClick={handleCronUpdate}
                >
                  {handleSubmitLoading ? (
                    <CircularProgress
                      sx={{ color: "white", ml: 1 }}
                      size={16}
                    />
                  ) : (
                    "Proceed"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>
    );
  }
}
